import { CSSResultGroup, TemplateResult, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { OutlineElement } from '@phase2/outline-core';
import componentStyles from './outline-popover.css.lit';
import iconStyles from '../outline-icon-baseline/outline-icon-baseline.css.lit';
import '../outline-button-baseline/outline-button-baseline';
import '../outline-icon-baseline/outline-icon-baseline';

/**
 * The OutlinePopover component
 *
 * @element outline-popover
 * @property {boolean} active - Whether the popover is visible.
 * @property {string} heading - The heading text of the popover.
 * @property {string} description - The description text of the popover.
 * @property {string} link - The URL link for more information.
 * @property {string} popoverId - The unique identifier for the popover.
 * @property {boolean} disableOthers - Disable other popovers when this one is visible.
 */
@customElement('outline-popover')
export class OutlinePopover extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles, iconStyles];

  /**
   * Whether the popover is visible.
   */
  @property({ type: Boolean, reflect: true })
  active = false;

  /**
   * The heading text of the popover.
   */
  @property({ type: String, reflect: true, attribute: 'heading' })
  heading = '';

  /**
   * The description text of the popover.
   */
  @property({ type: String, reflect: true, attribute: 'description' })
  description = '';

  /**
   * The URL link for more information.
   */
  @property({ type: String, reflect: true, attribute: 'link' })
  link = '';

  /**
   * The button label.
   */
  @property({ type: String, reflect: true, attribute: 'button-label' })
  buttonLabel = '';

  /**
   * The button class.
   */
  @property({ type: String, reflect: true, attribute: 'button-class' })
  buttonClass = '';

  /**
   * The popover class.
   */
  @property({ type: String, reflect: true, attribute: 'popover-class' })
  popoverClass = '';

  /**
   * The popover id.
   */
  @property({ type: String, reflect: true, attribute: 'popover-id' })
  popoverId = '';

  /**
   * Disable other popovers when this one is visible.
   */
  @property({ type: Boolean, reflect: true, attribute: 'disable-others' })
  disableOthers = false;

  render(): TemplateResult {
    return html`
      <div
        class="popover ${this.active ? '' : 'popover--is-hidden'} ${this
          .popoverClass}"
        id="${this.popoverId}"
      >
        <div class="popover__container">
          <div class="popover__content user-markup">
            ${this.heading
              ? html`<h3 class="popover__heading">${this.heading}</h3>`
              : ''}
            ${this.description
              ? html`<p class="popover__desc">${this.description}</p>`
              : ''}
            ${this.link
              ? html`
                  <a
                    class="btn primary small menu-secondary"
                    href="${this.link}"
                    aria-label="small link"
                  >
                    ${this.buttonLabel}
                    <div class="right-icon-container">
                      <outline-icon-baseline
                        name="eu-chevron"
                        library="system"
                        size=".75rem"
                      ></outline-icon-baseline>
                    </div>
                  </a>
                `
              : ''}
          </div>
        </div>
        <button
          aria-describedby="${this.popoverId}"
          class="popover-btn ${this.buttonClass}
          ${this.active ? `popover-btn--is-active` : ''}"
          @click="${this.toggle}"
          tabindex="0"
          type="button"
        >
          <outline-icon-baseline
            class="icon"
            library="system"
            name="${this.active ? 'eu-minus' : 'eu-plus'}"
            size="10px"
          ></outline-icon-baseline>
          <span class="show-for-sr">
            ${this.active
              ? `Hide information about ${this.heading}`
              : `Show information about ${this.heading}`}
          </span>
        </button>
      </div>
    `;
  }

  toggle() {
    // Always deactivate other popovers.
    const popovers = document.querySelectorAll('outline-popover');
    popovers.forEach(popover => {
      if (popover !== this) {
        (popover as OutlinePopover).active = false;
      }
    });

    // Toggle the current popover.
    this.active = !this.active;
  }
}
